import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { SourceTypeCategory } from 'webapp/enums/enums';
import { PopularLazyLoadSelector } from 'webapp/utils/common';

import makeArray from 'webapp/helpers/make-array';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import DataManagerConnectPanelComponent from 'webapp/components/data-manager/connect-panel';
import DataManagerGridItemComponent from 'webapp/components/data-manager/grid-item';
import DataManagerGridItemPlaceholderRequestSourceTypeComponent from 'webapp/components/data-manager/grid-item-placeholder-request-source-type';
import DataManagerNoResultsComponent from 'webapp/components/data-manager/no-results';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import OnboardingCommonSourceItemComponent from 'webapp/components/onboarding/common/source-item';
import SectionWithTogglerComponent from 'webapp/components/section-with-toggler';
import SubmitButtonComponent from 'webapp/components/submit-button';

import FilterSortAvailableSource from 'webapp/models-custom/filter-sort/available-source';
import type SourceType from 'webapp/models-custom/source-type';

export type SectionTogglerKey = 'popular' | 'other' | 'databasesAndWarehouses' | 'metricBuilder';

export interface SectionTogglerItem {
	isOpen: boolean;
	title: string;
}

export type SectionToggler = Record<SectionTogglerKey, SectionTogglerItem>;

interface Args {
	// Onboarding properties
	isOnboarding?: boolean;
	skipOnboarding?: () => void;
	toggleSourceTypeForOnboarding?: (sourceType: SourceType) => void;
	onboardingSelectedSourceTypes?: string[];
}

export default class DataManagerAvailableComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	model: SourceType[];
	mainFilterSort: FilterSortAvailableSource;

	@tracked sectionsToggler: SectionToggler = {
		popular: {
			isOpen: true,
			title: this.args.isOnboarding ? 'Popular' : 'POPULAR INTEGRATIONS',
		},
		other: {
			isOpen: true,
			title: this.args.isOnboarding ? 'Other' : 'OTHER INTEGRATIONS',
		},
		databasesAndWarehouses: {
			isOpen: true,
			title: this.args.isOnboarding
				? 'Databases and warehouses'
				: 'DATABASES AND WAREHOUSE INTEGRATIONS',
		},
		metricBuilder: { isOpen: true, title: 'CUSTOM API INTEGRATIONS' },
	};

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.model = this.mmSourceTypesService.connectableSourceTypeModels.filter((item) => {
			if (this.args.isOnboarding) {
				return !item.isHidden && !item.showInGroupOnly && !item.isWithCustomToken;
			}

			return !item.isHidden && !item.showInGroupOnly;
		});

		const filterSort = new FilterSortAvailableSource({
			selectors: PopularLazyLoadSelector.grid,
			getAllItems: () => {
				return this.model;
			},
		});

		this.mainFilterSort = filterSort;
		this.mainFilterSort.searchPlaceholder = this.args.isOnboarding
			? 'Search tools...'
			: 'Search...';
	}

	get noSearchResults(): boolean {
		return !!this.mainFilterSort.typedFilter && this.mainFilterSort.noResults;
	}

	get showRequestSourceOption(): boolean {
		return (
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins &&
			!this.noSearchResults
		);
	}

	// section-specific visibility getters
	get scrollableContainerSelector(): string | undefined {
		if (this.args.isOnboarding) {
			return '.integrations-list-wrapper';
		}

		return undefined;
	}

	get shouldHideRecommendedSection(): boolean {
		return !this.hasVisibleItems(this.recommendedIntegrationsFilterFn);
	}

	get shouldHidePopularSection(): boolean {
		return !this.hasVisibleItems(this.popularIntegrationsFilterFn);
	}

	get shouldHideOtherSection(): boolean {
		return !this.hasVisibleItems(this.otherIntegrationsFilterFn);
	}

	get shouldHideDatabasesAndWarehousesSection(): boolean {
		return !this.hasVisibleItems(this.databasesAndWarehousesIntegrationsFilterFn);
	}

	get shouldHideMetricBuilderSection(): boolean {
		return !this.hasVisibleItems(this.metricBuilderIntegrationsFilterFn);
	}

	@action
	resetFilter() {
		this.mainFilterSort.resetFilters(true);
	}

	@action
	requestSource(): void {
		this.mmSourceTypesService.openSourceRequestForm();
	}

	// filter functions
	recommendedIntegrationsFilterFn(items: SourceType[]) {
		return items.filter((item) => item.isRecommendedNotConnected);
	}

	@action
	popularIntegrationsFilterFn(items: SourceType[]) {
		return items.filter((item) => {
			const isNative = this.isNativeIntegration(item);

			if (!isNative) {
				return false;
			}

			if (this.flowTourOnboardingService.isBannerShown) {
				return item.isPopular && item.category === SourceTypeCategory.CLOUD;
			} else {
				return item.isPopular;
			}
		});
	}

	@action
	otherIntegrationsFilterFn(items: SourceType[]) {
		return items.filter((item) => {
			const isNative = this.isNativeIntegration(item);

			if (!isNative) {
				return false;
			}

			if (this.args.isOnboarding && item.isWithCustomToken) {
				return false;
			}

			return !item.isPopular;
		});
	}

	@action
	toggleSection(value: SectionTogglerKey): void {
		if (this.args.isOnboarding) {
			return;
		}

		const openState = this.sectionsToggler[value].isOpen;

		this.sectionsToggler = {
			...this.sectionsToggler,
			[value]: {
				...this.sectionsToggler[value],
				isOpen: !openState,
			},
		};
	}

	databasesAndWarehousesIntegrationsFilterFn(items: SourceType[]) {
		return items.filter(
			(item) =>
				!item.isRecommendedNotConnected &&
				!item.isGoogleSheets &&
				!item.metricBuilderIntegration &&
				item.isSqlishConnector,
		);
	}

	metricBuilderIntegrationsFilterFn(items: SourceType[]) {
		return items.filter((item) => item.metricBuilderIntegration);
	}

	private isNativeIntegration(item: SourceType): boolean {
		return (
			item.isGoogleSheets ||
			(!item.isRecommendedNotConnected &&
				!item.metricBuilderIntegration &&
				!item.isSqlishConnector)
		);
	}

	// check if a section has any visible items after filtering
	private hasVisibleItems(filterFn: (items: SourceType[]) => SourceType[]): boolean {
		const filteredItems = this.mainFilterSort.results;
		return filterFn(filteredItems).length > 0;
	}
	<template>
		<div id="integrations-list">
			<FilterLineProxyComponent @model={{this.mainFilterSort}} />

			<div class="integrations-list-wrapper">
				{{#if this.noSearchResults}}
					{{#if @skipOnboarding}}
						<div class="no-results">
							No results found for
							<span>{{this.mainFilterSort.typedFilter}}</span>, but there are other
							ways to bring in your data.
						</div>
						<div class="no-results-actions">
							<div class="no-results-actions__header">
								<strong>Explore other ways to connect your data (later in-app)</strong>
								<SubmitButtonComponent
									@text="Skip and go to app &gt;"
									@noDelay={{true}}
									@onTrigger={{@skipOnboarding}}
									@theme="blue-outlined"
								/>
							</div>
							<div class="no-results-actions__content">
								<DataManagerConnectPanelComponent
									@model={{this.model}}
									@preventAppendingBodyClass={{true}}
									@isOnboarding={{@isOnboarding}}
								/>
							</div>
						</div>
					{{else}}
						<DataManagerNoResultsComponent
							@model={{this.model}}
							@filterSort={{this.mainFilterSort}}
							@onOpenSourceRequest={{this.requestSource}}
							@resetFilter={{this.resetFilter}}
						/>
					{{/if}}
				{{else}}
					{{! Recommended integrations section }}
					<CommonListLazyLoadComponent
						@classNames="lazy-grid"
						@filterSort={{this.mainFilterSort}}
						@filterExtraFn={{this.recommendedIntegrationsFilterFn}}
						@initialRowCount={{10}}
						@loadMoreRowCount={{5}}
						@animationProperty="id"
						@scrollableContainerSelector={{this.scrollableContainerSelector}}
						@autoLoaded={{true}}
						@bottomDistance={{700}}
						as |visibleItems exporter|
					>
						<ul
							class="flex-ct flex-ct--size-source flex-ct--size-source-condensed recommended-integrations-list"
							{{visibleCheckContainer}}
						>
							{{#each visibleItems as |item|}}
								{{#if @isOnboarding}}
									<OnboardingCommonSourceItemComponent
										@model={{item}}
										@selectedItems={{@onboardingSelectedSourceTypes}}
										@onClick={{@toggleSourceTypeForOnboarding}}
									/>
								{{else}}
									<DataManagerGridItemComponent
										@model={{item}}
										@trackingOrigin="datamanager"
										@showConnectLoader={{true}}
									/>
								{{/if}}
							{{/each}}
							{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
							{{#each (makeArray length=exporter.itemsPerRow)}}
								<li class="flex-item flex-item--fake"></li>
							{{/each}}
						</ul>
						{{#unless exporter.areAllItemsLoaded}}
							<CommonListLoaderComponent />
						{{/unless}}
					</CommonListLazyLoadComponent>

					{{! Popular integrations section }}
					<SectionWithTogglerComponent
						@isOpen={{this.sectionsToggler.popular.isOpen}}
						@title={{this.sectionsToggler.popular.title}}
						@toggle={{fn this.toggleSection "popular"}}
						@isSectionTogglerHidden={{this.shouldHidePopularSection}}
					>
						<CommonListLazyLoadComponent
							@classNames="lazy-grid"
							@filterSort={{this.mainFilterSort}}
							@filterExtraFn={{this.popularIntegrationsFilterFn}}
							@initialRowCount={{10}}
							@loadMoreRowCount={{5}}
							@animationProperty="id"
							@scrollableContainerSelector={{this.scrollableContainerSelector}}
							@autoLoaded={{true}}
							@bottomDistance={{700}}
							as |visibleItems exporter|
						>
							<ul
								class="flex-ct flex-ct--size-source flex-ct--size-source-condensed popular-integrations-list"
								{{visibleCheckContainer}}
							>
								{{#each visibleItems as |item|}}
									{{#if @isOnboarding}}
										<OnboardingCommonSourceItemComponent
											@model={{item}}
											@selectedItems={{@onboardingSelectedSourceTypes}}
											@onClick={{@toggleSourceTypeForOnboarding}}
										/>
									{{else}}
										<DataManagerGridItemComponent
											@model={{item}}
											@trackingOrigin="datamanager"
											@showConnectLoader={{true}}
										/>
									{{/if}}
								{{/each}}
								{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
								{{#each (makeArray length=exporter.itemsPerRow)}}
									<li class="flex-item flex-item--fake"></li>
								{{/each}}
							</ul>
							{{#unless exporter.areAllItemsLoaded}}
								<CommonListLoaderComponent />
							{{/unless}}
						</CommonListLazyLoadComponent>
					</SectionWithTogglerComponent>

					{{! Other integrations section }}
					<SectionWithTogglerComponent
						@isOpen={{this.sectionsToggler.other.isOpen}}
						@title={{this.sectionsToggler.other.title}}
						@toggle={{fn this.toggleSection "other"}}
						@isSectionTogglerHidden={{this.shouldHideOtherSection}}
					>
						<CommonListLazyLoadComponent
							@classNames="lazy-grid"
							@filterSort={{this.mainFilterSort}}
							@filterExtraFn={{this.otherIntegrationsFilterFn}}
							@initialRowCount={{10}}
							@loadMoreRowCount={{5}}
							@animationProperty="id"
							@scrollableContainerSelector={{this.scrollableContainerSelector}}
							@autoLoaded={{true}}
							@bottomDistance={{700}}
							as |visibleItems exporter|
						>
							<ul
								class="flex-ct flex-ct--size-source flex-ct--size-source-condensed other-integrations-list"
								{{visibleCheckContainer}}
							>
								{{#each visibleItems as |item|}}
									{{#if @isOnboarding}}
										<OnboardingCommonSourceItemComponent
											@model={{item}}
											@selectedItems={{@onboardingSelectedSourceTypes}}
											@onClick={{@toggleSourceTypeForOnboarding}}
										/>
									{{else}}
										<DataManagerGridItemComponent
											@model={{item}}
											@trackingOrigin="datamanager"
											@showConnectLoader={{true}}
										/>
									{{/if}}
								{{/each}}
								{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
								{{#each (makeArray length=exporter.itemsPerRow)}}
									<li class="flex-item flex-item--fake"></li>
								{{/each}}
							</ul>
							{{#unless exporter.areAllItemsLoaded}}
								<CommonListLoaderComponent />
							{{/unless}}
						</CommonListLazyLoadComponent>
					</SectionWithTogglerComponent>

					{{! SQL integrations section }}
					<SectionWithTogglerComponent
						@isOpen={{this.sectionsToggler.databasesAndWarehouses.isOpen}}
						@title={{this.sectionsToggler.databasesAndWarehouses.title}}
						@toggle={{fn this.toggleSection "databasesAndWarehouses"}}
						@isSectionTogglerHidden={{this.shouldHideDatabasesAndWarehousesSection}}
					>
						<CommonListLazyLoadComponent
							@classNames="lazy-grid"
							@filterSort={{this.mainFilterSort}}
							@filterExtraFn={{this.databasesAndWarehousesIntegrationsFilterFn}}
							@initialRowCount={{10}}
							@loadMoreRowCount={{5}}
							@animationProperty="id"
							@scrollableContainerSelector={{this.scrollableContainerSelector}}
							@autoLoaded={{true}}
							@bottomDistance={{700}}
							as |visibleItems exporter|
						>
							<ul
								class="flex-ct flex-ct--size-source flex-ct--size-source-condensed sql-integrations-list"
								{{visibleCheckContainer}}
							>
								{{#each visibleItems as |item|}}
									{{#if @isOnboarding}}
										<OnboardingCommonSourceItemComponent
											@model={{item}}
											@selectedItems={{@onboardingSelectedSourceTypes}}
											@onClick={{@toggleSourceTypeForOnboarding}}
										/>
									{{else}}
										<DataManagerGridItemComponent
											@model={{item}}
											@trackingOrigin="datamanager"
											@showConnectLoader={{true}}
										/>
									{{/if}}
								{{/each}}
								{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
								{{#each (makeArray length=exporter.itemsPerRow)}}
									<li class="flex-item flex-item--fake"></li>
								{{/each}}
							</ul>
							{{#unless exporter.areAllItemsLoaded}}
								<CommonListLoaderComponent />
							{{/unless}}
						</CommonListLazyLoadComponent>
					</SectionWithTogglerComponent>

					{{#unless @isOnboarding}}
						{{! Metric builder integrations section }}
						<SectionWithTogglerComponent
							@isOpen={{this.sectionsToggler.metricBuilder.isOpen}}
							@title={{this.sectionsToggler.metricBuilder.title}}
							@toggle={{fn this.toggleSection "metricBuilder"}}
							@isSectionTogglerHidden={{this.shouldHideMetricBuilderSection}}
						>
							<CommonListLazyLoadComponent
								@classNames="lazy-grid"
								@filterSort={{this.mainFilterSort}}
								@filterExtraFn={{this.metricBuilderIntegrationsFilterFn}}
								@initialRowCount={{10}}
								@loadMoreRowCount={{5}}
								@animationProperty="id"
								@scrollableContainerSelector={{this.scrollableContainerSelector}}
								@autoLoaded={{true}}
								@bottomDistance={{700}}
								as |visibleItems exporter|
							>
								<ul
									class="flex-ct flex-ct--size-source flex-ct--size-source-condensed metric-builder-integrations-list"
									{{visibleCheckContainer}}
								>
									{{#each visibleItems as |item|}}
										<DataManagerGridItemComponent
											@model={{item}}
											@trackingOrigin="datamanager"
											@showConnectLoader={{true}}
										/>
									{{/each}}
									{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
									{{#each (makeArray length=exporter.itemsPerRow)}}
										<li class="flex-item flex-item--fake"></li>
									{{/each}}
								</ul>
								{{#unless exporter.areAllItemsLoaded}}
									<CommonListLoaderComponent />
								{{/unless}}
							</CommonListLazyLoadComponent>
						</SectionWithTogglerComponent>

						{{#if this.showRequestSourceOption}}
							<ul class="flex-ct flex-ct--size-source flex-ct--size-source-condensed">
								<DataManagerGridItemPlaceholderRequestSourceTypeComponent
									@onSourceRequest={{this.requestSource}}
								/>

								{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
								{{#each (makeArray length=26)}}
									<li class="flex-item flex-item--fake"></li>
								{{/each}}
							</ul>
						{{/if}}
					{{/unless}}
				{{/if}}

				{{#unless @isOnboarding}}
					<DataManagerConnectPanelComponent @model={{this.model}} />
				{{/unless}}
			</div>
		</div>
	</template>
}
